/**
 * Shared react-markdown `components` override that rewrites relative image
 * `src` attributes to hit the backend image proxy endpoint:
 *   `/api/knowledge-objects/:id/image/<relative-path>`
 *
 * Images are fetched via an authenticated request (Bearer token from
 * localStorage) and rendered using blob URLs, since browser-native <img>
 * requests don't carry the Authorization header.
 *
 * Absolute URLs (http/https/data:) are left untouched.
 * Absolute filesystem paths (starting with /) that leaked from Docling are
 * normalised to a relative form by extracting the portion after known
 * artifact directory names.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Components } from 'react-markdown';
import { env } from '@/config/env';
import { authHeader } from '@/lib/auth/token-store';

/**
 * Small component that fetches an image via an authenticated request
 * and renders it using a blob: URL.
 */
function AuthenticatedImage({ src, alt }: { src: string; alt: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) return;

    // For absolute web URLs and data URIs, skip authenticated fetch
    if (src.startsWith('http') || src.startsWith('data:') || src.startsWith('blob:')) {
      setBlobUrl(src);
      return;
    }

    let cancelled = false;

    const fetchImage = async () => {
      try {
        // In-memory bearer (never storage) — see lib/auth/token-store.ts.
        const headers: Record<string, string> = { ...authHeader() };

        // Build the full URL to fetch from.
        // In dev, env.apiUrl = "http://localhost:3001" — prefix it so the
        // request reaches the backend, not the Vite dev server.
        // In prod, env.apiUrl is empty and nginx proxies /api/* to the backend.
        let fetchUrl = src;
        if (src.startsWith('/api') && env.apiUrl) {
          fetchUrl = `${env.apiUrl}${src}`;
        }

        console.debug(`[AuthenticatedImage] Fetching: ${fetchUrl}`);

        const response = await fetch(fetchUrl, {
          headers,
          mode: 'cors',
        });

        if (cancelled) return;

        if (!response.ok) {
          console.warn(`[AuthenticatedImage] Failed to load: ${fetchUrl} (${response.status})`);
          setError(true);
          return;
        }

        const blob = await response.blob();
        if (!cancelled) {
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          console.warn('[AuthenticatedImage] Fetch error:', (e as Error).message);
          setError(true);
        }
      }
    };

    fetchImage();

    return () => {
      cancelled = true;
      if (blobUrl && blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (error) {
    return (
      <span style={{ color: 'var(--cds-text-secondary)', fontStyle: 'italic' }}>
        [Image unavailable: {alt || 'image'}]
      </span>
    );
  }

  if (!blobUrl) {
    return (
      <span style={{ display: 'inline-block', minHeight: '2rem', color: 'var(--cds-text-secondary)' }}>
        Loading image…
      </span>
    );
  }

  return (
    <img
      src={blobUrl}
      alt={alt}
      style={{ maxWidth: '100%', height: 'auto' }}
      loading="lazy"
    />
  );
}

export function useMarkdownComponents(knowledgeObjectId: string): Components {
  return useMemo<Components>(() => ({
    img: ({ src, alt }) => {
      let resolvedSrc = src ?? '';

      // If the src already contains our API image route prefix (e.g. from a
      // prior failed rewrite stored in the chunk content), strip the prefix
      // back out so we can resolve cleanly.
      const apiPrefix = `/api/knowledge-objects/${knowledgeObjectId}/image/`;
      if (resolvedSrc.startsWith(apiPrefix)) {
        resolvedSrc = resolvedSrc.slice(apiPrefix.length);
      }

      // Only rewrite paths that look like relative file references.
      // Leave absolute web URLs (http/https) and data URIs alone.
      if (resolvedSrc && !resolvedSrc.startsWith('http') && !resolvedSrc.startsWith('data:')) {
        // If the path is an absolute filesystem path (starts with /) that leaked
        // from Docling, strip it down to the filename or the last meaningful
        // segments (e.g. "artifacts/image_xxx.png").
        if (resolvedSrc.startsWith('/')) {
          // Try to extract the relative portion after known artifact dirs
          const artifactsIdx = resolvedSrc.indexOf('/artifacts/');
          const imagesIdx = resolvedSrc.indexOf('/images/');
          const figuresIdx = resolvedSrc.indexOf('/figures/');
          const bestIdx = Math.max(artifactsIdx, imagesIdx, figuresIdx);
          if (bestIdx >= 0) {
            // +1 to skip the leading slash so we get "artifacts/..." not "/artifacts/..."
            resolvedSrc = resolvedSrc.slice(bestIdx + 1);
          } else {
            // Last resort: use just the filename
            const parts = resolvedSrc.split('/');
            resolvedSrc = parts[parts.length - 1];
          }
        }
        resolvedSrc = `${apiPrefix}${resolvedSrc}`;
      }

      return <AuthenticatedImage src={resolvedSrc} alt={alt ?? ''} />;
    },
  }), [knowledgeObjectId]);
}
